import React, { useState, useEffect } from 'react'
import PageHeader from '@/components/PageHeader'
import api, { imgSrc } from '@/api/index'

const SECTOR_META = {
  'residential':       { t: 'Private homes & residences',           blurb: 'A large share of our work is for individual families — new builds, retrofits, and finishing work on private homes across Kathmandu and beyond.' },
  'schools-hospitals': { t: 'Schools, hospitals & community',       blurb: 'We have helped build, retrofit and extend institutions Nepali families rely on every day.' },
  'ngos':              { t: 'NGOs, foundations & development',      blurb: 'A long roster of NGOs and development organisations have engaged SMT, often on multi-year framework agreements.' },
  'international':     { t: 'International agencies & embassies',   blurb: 'Major international organisations and foreign missions also trust SMT for projects requiring the highest engineering and security standards.' },
}

const SECTOR_ORDER = ['international', 'schools-hospitals', 'ngos', 'residential']

function groupClients(clients) {
  const map = {}
  clients.forEach(c => {
    const meta = SECTOR_META[c.sector] || { t: c.sector, blurb: '' }
    if (!map[c.sector]) map[c.sector] = { ...meta, list: [] }
    map[c.sector].list.push(c)
  })
  // Return in defined sector order, then any extras
  const ordered = SECTOR_ORDER.filter(k => map[k]).map(k => map[k])
  Object.keys(map).forEach(k => { if (!SECTOR_ORDER.includes(k)) ordered.push(map[k]) })
  return ordered.filter(g => g.list.length > 0)
}

function ClientCard({ c }) {
  const [imgErr, setImgErr] = useState(false)
  const logoSrc = c.logo ? imgSrc(c.logo) : null
  const initials = c.name.split(' ').slice(0, 2).map(w => w[0]).join('')

  return (
    <div className="panel lift" style={{
      padding: 24, display: 'grid', gridTemplateColumns: '64px 1fr', gap: 18, alignItems: 'start',
    }}>
      {/* Logo box — white background, exactly as original */}
      <div style={{
        width: 64, height: 64, borderRadius: 12,
        background: '#fff', border: '1px solid var(--line-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0,
      }}>
        {logoSrc && !imgErr ? (
          <img src={logoSrc} alt={c.name}
            onError={() => setImgErr(true)}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 6 }} />
        ) : (
          <span style={{ fontFamily: 'var(--space)', fontSize: 16, fontWeight: 700, color: 'var(--fg-2)' }}>
            {initials}
          </span>
        )}
      </div>

      {/* Name + description */}
      <div>
        <div style={{ fontFamily: 'var(--space)', fontSize: 16, fontWeight: 600, color: 'var(--fg-0)', lineHeight: 1.3 }}>
          {c.name}
        </div>
        {c.description && (
          <div style={{ fontSize: 13.5, color: 'var(--fg-2)', marginTop: 6, lineHeight: 1.55 }}>
            {c.description}
          </div>
        )}
      </div>
    </div>
  )
}

export default function ClientsPage() {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/clients')
      .then(({ data }) => setGroups(groupClients(data.data)))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="page">
      <PageHeader eyebrow="Our Clients"
        title={<>The people we&rsquo;ve <span style={{ color: 'var(--accent)' }}>built for</span>.</>}
        sub="Families, schools, hospitals, NGOs, government bodies and major international organisations — many have come back to us across multiple projects and multiple decades."
        badge="UN agencies · Embassies · Hospitals · Schools · Families"
        variant="clients" />

      <section style={{ padding: '64px 0' }}>
        <div className="container">

          {/* Loading skeleton */}
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 80 }}>
              {[0, 1].map(i => (
                <div key={i}>
                  <div style={{ height: 28, width: 240, background: 'var(--bg-2)', borderRadius: 6, marginBottom: 32, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                    {[0,1,2,3].map(j => (
                      <div key={j} className="panel" style={{ padding: 24, display: 'grid', gridTemplateColumns: '64px 1fr', gap: 18 }}>
                        <div style={{ width: 64, height: 64, borderRadius: 12, background: 'var(--bg-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <div>
                          <div style={{ height: 14, width: '70%', background: 'var(--bg-2)', borderRadius: 6, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                          <div style={{ height: 11, width: '90%', background: 'var(--bg-2)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.45}}`}</style>
            </div>
          )}

          {/* Empty state */}
          {!loading && groups.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--fg-2)' }}>
              <div className="mono" style={{ fontSize: 13, color: 'var(--fg-3)' }}>No clients yet</div>
              <p style={{ marginTop: 8, fontSize: 14 }}>Add clients from the admin panel to display them here.</p>
            </div>
          )}

          {/* Sector groups */}
          {!loading && groups.map((g, gi) => (
            <div key={g.t} style={{ marginTop: gi === 0 ? 0 : 80 }}>
              {/* Sector header */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 64, alignItems: 'start', paddingBottom: 32, borderBottom: '1px solid var(--line)' }} className="client-header">
                <div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '6px 12px', background: 'var(--accent-soft)', borderRadius: 999 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-deep)', letterSpacing: '0.02em' }}>Sector</span>
                  </div>
                  <h3 className="display" style={{ fontSize: 'clamp(28px, 4vw, 44px)', marginTop: 14, color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.02em' }}>{g.t}</h3>
                </div>
                <p style={{ fontSize: 16, color: 'var(--fg-1)', lineHeight: 1.6, maxWidth: 560, paddingTop: 8 }}>{g.blurb}</p>
              </div>

              {/* Client cards grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginTop: 32 }} className="client-grid">
                {g.list.map(c => <ClientCard key={c._id || c.name} c={c} />)}
              </div>
            </div>
          ))}
        </div>

        <style>{`
          @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
          @media (max-width: 900px) {
            .client-header { grid-template-columns: 1fr !important; gap: 16px !important; }
            .client-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>
    </div>
  )
}
